/**
 * Stored FFU output the scorer reads: drop 1, stretch, and extras.
 * Fields the scorer schema does not have stay on the same objects.
 * Nothing here is filled in that the resolved facts do not already state.
 */

import { z } from 'zod';
import { buildingPartRequirementAggregatedSchema } from './building-part-requirement.model';
import {
  EXTRACTED_BUILDING_CONNECTION_RELATIONSHIPS,
  EXTRACTED_PROGRAM_SHARE_NODON_DESCRIPTION,
  MAPPED_EXTRACTED_PROGRAM_TYPES,
  READ_FROMS,
  READ_SOURCES,
  ROOF_FORM_TYPES,
  WINDOW_RATIO_DETERMINED_BY,
  extractedApartmentFactsSchema,
  extractedOtherAreaFactsSchema,
} from './extracted-design-facts.model';
import { RESIDENTIAL_PROGRAM_FORM } from './extracted-design-facts.model';
import { northMethodSchema } from './project-file-drawing.model';
import type { Shape } from './design/storey.model';
import { isPolygon2D } from '../helpers/geometry/geometry.helpers';
import {
  specialStoreyKindSchema,
  storeyCountSchema,
} from './storey-level.model';

const point2DSchema = z.tuple([z.number(), z.number()]);

/** Design {@link Shape} for FFU output; `points` has at least three vertices. */
export const ffuShapeSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  points: z.array(point2DSchema).min(3),
  cutout: z.boolean().optional(),
});

export type FfuShape = z.infer<typeof ffuShapeSchema>;

/** Parses pipeline JSON into a design {@link Shape}. */
export const toFfuShape = (value: FfuShape): Shape => {
  if (!isPolygon2D(value.points)) {
    throw new Error(`Invalid FFU shape points for id ${value.id}`);
  }
  return {
    id: value.id,
    name: value.name,
    points: value.points,
    cutout: value.cutout,
  };
};

const readSchema = z.object({
  source: z.enum(READ_SOURCES),
  from: z.enum(READ_FROMS),
});

export const ffuAreasSchema = z.object({
  gfa: z.number().nullable(),
  gfa_above_ground: z.number().nullable(),
  livingAreaM2: z.number().nullable(),
  premisesAreaM2: z.number().nullable(),
  footprintM2: z.number().nullable(),
});

export type FfuAreas = z.infer<typeof ffuAreasSchema>;

export const emptyFfuAreas = (): FfuAreas => ({
  gfa: null,
  gfa_above_ground: null,
  livingAreaM2: null,
  premisesAreaM2: null,
  footprintM2: null,
});

export const ffuWindowRatioSchema = z.object({
  value: z.number(),
  determined_by: z.enum(WINDOW_RATIO_DETERMINED_BY),
});

export type FfuWindowRatio = z.infer<typeof ffuWindowRatioSchema>;

/** Stretch roof. `formRead` / `pitchRead` have no scorer field; they stay. */
export const ffuRoofSchema = z.object({
  form: z.enum(ROOF_FORM_TYPES),
  pitchDeg: z.number().nullable(),
  formRead: readSchema.nullable(),
  pitchRead: readSchema.nullable(),
});

export type FfuRoof = z.infer<typeof ffuRoofSchema>;

/**
 * One material on the facade or the roof, with its portion of that part.
 * Written by the final output from the element requirement rows.
 */
export const ffuMaterialShareSchema = z.object({
  name: z.string(),
  share: z.number(),
  sbef: z.string(),
  /** Nodon generic product ids the name matched. Empty when none did. */
  allowed: z.array(z.string()),
});

export type FfuMaterialShare = z.infer<typeof ffuMaterialShareSchema>;

const ffuMappedProgramSchema = z.object({
  type: z.enum(MAPPED_EXTRACTED_PROGRAM_TYPES),
  share: z
    .number()
    .nullable()
    .describe(EXTRACTED_PROGRAM_SHARE_NODON_DESCRIPTION),
  /**
   * Stated square metres. The scorer reads {@link share}. The square metres
   * stay when the document stated them, including when share cannot be formed.
   */
  areaM2: z.number().nullable(),
});

const ffuOtherProgramSchema = z.object({
  type: z.literal('other'),
  share: z
    .number()
    .nullable()
    .describe(EXTRACTED_PROGRAM_SHARE_NODON_DESCRIPTION),
  areaM2: z.number().nullable(),
  /** Extras: the printed word. Null when the document did not print one. */
  label: z.string().nullable(),
});

export const ffuProgramSchema = z.discriminatedUnion('type', [
  ffuMappedProgramSchema,
  ffuOtherProgramSchema,
]);

export type FfuProgram = z.infer<typeof ffuProgramSchema>;

const ffuStoreyIndexSchema = z.union([
  z.number().int(),
  z.literal('*'),
  z.object({ from: z.number().int(), to: z.number().int() }),
]);

export const ffuStoreySchema = z.object({
  /** Bare index when the storey has one. Null for a range, a typical floor, or a roof plan. */
  index: z.number().int().nullable(),
  name: z.string().nullable(),
  /** Printed plan numbers. Not a scorer field. */
  planNumbers: z.array(z.number().int()),
  height: z.number().nullable(),
  /** Room height. Scorer extras; not on the design storey type. */
  inner_height: z.number().nullable(),
  gfa: z.number().nullable(),
  elevation: z.number().nullable(),
  /** Printed plushöjd. Not the ground anchor, and not a scorer field. */
  elevationMarkM: z.number().nullable(),
  activities: z.array(ffuProgramSchema),
  /** Extras. Null when no single plan sheet is this storey's. */
  planId: z.string().nullable(),
  /** Extras. `data_gap` when no drawing can be used. Otherwise null. */
  drawing: z.enum(['data_gap']).nullable(),
  /**
   * Extras. A storey of a special kind; absent on an ordinary storey. An attic
   * borrows no footprint, and without a drawing or a storey area of its own it
   * is only roof.
   */
  kind: specialStoreyKindSchema.optional(),
  /**
   * Storey footprint geometry (design `shapes`). Milestone `storey-shapes`.
   * Inner courtyard: solid outer ring + `cutout: true` on the yard polygon.
   */
  shapes: z.array(ffuShapeSchema).optional(),
  /**
   * Solid-shape centroid offset from storey 0 (m), same Nodon origin. Milestone `storey-shapes`.
   */
  shape_stack_offset_m: z.object({ x: z.number(), y: z.number() }).optional(),
});

export type FfuStorey = z.infer<typeof ffuStoreySchema>;

export const ffuPlanSchema = z.object({
  id: z.string(),
  file: z.string(),
  page: z.number().int().positive(),
  label: z.string().nullable(),
  storeyIndex: ffuStoreyIndexSchema.nullable(),
  storeyKind: z.string().nullable(),
  scale: z.object({
    ratio: z.string().nullable(),
    paper: z.string().nullable(),
  }),
});

export type FfuPlan = z.infer<typeof ffuPlanSchema>;

export const ffuPartSchema = z.object({
  name: z.string(),
  /** Not a scorer field on a part. */
  storeyCount: storeyCountSchema.nullable(),
  /**
   * Part totals have no scorer field. Same English keys as the building areas.
   */
  areas: ffuAreasSchema,
  apartmentCount: z.number().nullable(),
  apartments: z.array(extractedApartmentFactsSchema),
  otherAreas: z.array(extractedOtherAreaFactsSchema),
  storeys: z.array(ffuStoreySchema),
  /** Building key the part came from. Not a scorer field. */
  sourceBuildingKey: z.string().nullable(),
});

export type FfuPart = z.infer<typeof ffuPartSchema>;

const ffuConnectionSchema = z.object({
  relationship: z.enum(EXTRACTED_BUILDING_CONNECTION_RELATIONSHIPS),
  /** Not a scorer field. */
  note: z.string(),
});

/**
 * The bottom storey's plan page placed under the building's outline: the
 * site model's corners on the page fitted to the final outline (DEV-3745).
 * Not a scorer field.
 */
export const ffuUnderlaySchema = z.object({
  fileId: z.string(),
  pageIndex: z.number().int(),
  /** Site model storey the page was read for. */
  storeyIndex: z.number().int(),
  /** The plan's title on the page. */
  label: z.string().nullable(),
  /** Page raster size the corners were read on. */
  widthPx: z.number(),
  heightPx: z.number(),
  /** Paper size of a PDF page. Null for an image upload. */
  paperWidthMm: z.number().nullable(),
  paperHeightMm: z.number().nullable(),
  /** Page centre in the building's frame (m). */
  centerM: point2DSchema,
  widthM: z.number(),
  heightM: z.number(),
  /** Clockwise, as on a blueprint underlay. From the corners. */
  rotationDeg: z.number(),
  /**
   * How the site model knew the page's north, which the turn is checked
   * against. Absent on underlays stored before.
   */
  northMethod: northMethodSchema.optional(),
  /** Fitted scale against the printed one. Null when the page states none. */
  scale: z.number().nullable(),
  /** Median corner error (m). */
  residualM: z.number(),
  /** False when the corners miss the outline; the project gets no page then. */
  placed: z.boolean(),
  /** What the checks found, for the admin view and the logs. */
  notes: z.array(z.string()),
});

export type FfuUnderlay = z.infer<typeof ffuUnderlaySchema>;

/**
 * A site plan page placed under a group of buildings: their outlines on it
 * fitted to the final outlines at the printed scale (DEV-3745). Not a scorer
 * field.
 */
export const ffuSitePlanUnderlaySchema = z.object({
  fileId: z.string(),
  pageIndex: z.number().int(),
  /** The plan's title on the page. */
  label: z.string().nullable(),
  /** Paper size of a PDF page. Null for an image upload. */
  paperWidthMm: z.number().nullable(),
  paperHeightMm: z.number().nullable(),
  /** Page centre in the design frame (m). */
  centerM: point2DSchema,
  widthM: z.number(),
  heightM: z.number(),
  /** Clockwise, as on a blueprint underlay. */
  rotationDeg: z.number(),
  /** Output indices of the buildings whose outline on the page the fit used. */
  buildingIndices: z.array(z.number().int()),
  /** Median distance from an outline corner on the page to the final outline (m). */
  residualM: z.number(),
});

export type FfuSitePlanUnderlay = z.infer<typeof ffuSitePlanUnderlaySchema>;

/**
 * An obvious error in the final output that the correction call found and
 * code fixed (DEV-4001). Not a scorer field.
 */
export const ffuCorrectionSchema = z.object({
  /** Index of the building in the output it was made on. */
  buildingIndex: z.number().int(),
  buildingName: z.string().nullable(),
  kind: z.enum(['turn', 'move', 'storeys', 'outline']),
  /** Turn about the building's own centre, clockwise. */
  turnDeg: z.number().nullable(),
  /** Move in the design frame (m). */
  moveM: point2DSchema.nullable(),
  /** The storey counts the building was given. */
  storeysAboveGround: z.number().int().nullable(),
  storeysBelowGround: z.number().int().nullable(),
  /** The storey whose outline was redrawn. */
  storeyIndex: z.number().int().nullable().optional(),
  /** The redrawn outline's shape id and its corners in the design frame (m). */
  shapeId: z.string().nullable().optional(),
  outlineM: z.array(point2DSchema).nullable().optional(),
  /** What in the documents shows the error, as the model put it. */
  reason: z.string(),
});

export type FfuCorrection = z.infer<typeof ffuCorrectionSchema>;

export const ffuBuildingSchema = z.object({
  id: z.string().nullable(),
  /** Site model buildings only. Absent when the site model gave no page corners. */
  underlay: ffuUnderlaySchema.nullable().optional(),
  /** House name. The scorer pairs buildings on content and does not read this. */
  buildingName: z.string().nullable(),
  /** Alias keys folded into {@link id}. Not a scorer field. */
  instanceKeys: z.array(z.string()),
  /** Not a scorer field. */
  storeyCount: storeyCountSchema.nullable(),
  properties: z.object({
    window_ratio: ffuWindowRatioSchema.nullable(),
  }),
  geometry: z.object({
    areas: ffuAreasSchema,
    parts: z.array(ffuPartSchema),
    storeys: z.array(ffuStoreySchema),
    roof: ffuRoofSchema.nullable(),
    /** Final output only: facade materials whose shares sum to 1. */
    facadeMaterials: z.array(ffuMaterialShareSchema).optional(),
    /** Final output only: roof covering materials whose shares sum to 1. */
    roofMaterials: z.array(ffuMaterialShareSchema).optional(),
  }),
  slabThicknessMm: z.number().nullable(),
  /** Not a scorer field. */
  beamSpanM: z.number().nullable(),
  /** Not a scorer field. Apartments are the extras list. */
  apartmentCount: z.number().nullable(),
  apartments: z.array(extractedApartmentFactsSchema),
  otherAreas: z.array(extractedOtherAreaFactsSchema),
  connection: ffuConnectionSchema.nullable(),
  /** Not a scorer field. The scorer reads {@link ffuBuildingSchema.activities}. */
  residential: z
    .object({
      livingAreaPerApartmentM2: z.number().nullable(),
      balconyAreaPerApartmentM2: z.number().nullable(),
      bathroomAreaPerApartmentM2: z.number().nullable().optional(),
      programType: z.enum(RESIDENTIAL_PROGRAM_FORM).nullable().optional(),
    })
    .nullable(),
  /**
   * Design activities. Properties sit here once. Written only from
   * {@link ffuBuildingSchema.residential} and {@link ffuBuildingSchema.stairwells}
   * the pipeline already chose.
   */
  activities: z
    .array(
      z.object({
        type: z.enum(['residential', 'stairwells']),
        properties: z.object({
          program_type: z.enum(RESIDENTIAL_PROGRAM_FORM).nullable().optional(),
          living_area_per_apartment: z.number().nullable().optional(),
          balcony_area_per_apartment: z.number().nullable().optional(),
          bathroom_area_per_apartment: z.number().nullable().optional(),
          apartments_per_stairwell_per_storey: z.number().nullable().optional(),
          elevators_per_stairwell: z.number().nullable().optional(),
        }),
      }),
    )
    .optional(),
  /** Not a scorer field. */
  stairwells: z
    .object({
      apartmentsPerStairwellPerStorey: z.number().nullable(),
      elevatorsPerStairwell: z.number().nullable(),
    })
    .nullable(),
  plans: z.array(ffuPlanSchema),
  /**
   * Element rows whose `buildingId` is this house. Whole-building caps stay
   * on the batch list: the scorer has no fields for them yet.
   */
  requirements: z.array(buildingPartRequirementAggregatedSchema),
});

export type FfuBuilding = z.infer<typeof ffuBuildingSchema>;

/**
 * How precisely the project was placed from the site the FFU names, finest
 * first: the plot itself down to the county.
 */
export const FFU_SITE_PRECISIONS = [
  'plot',
  'address',
  'block',
  'street',
  'district',
  'locality',
  'municipality',
  'region',
] as const;

export const ffuSitePrecisionSchema = z.enum(FFU_SITE_PRECISIONS);

export type FfuSitePrecision = z.infer<typeof ffuSitePrecisionSchema>;

/** Where the project was placed on the map, and how precisely. */
export type FfuSitePlacement = {
  /** Null when the lookup did not say. */
  precision: FfuSitePrecision | null;
  /** The place the coordinates point at, e.g. "Blackeberg, Stockholm". */
  placeName: string | null;
};

export const ffuOutputFactsSchema = z.object({
  name: z.string().nullable(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  /** Not a scorer field. */
  storeyCount: z.number().nullable(),
  /** Not a scorer field. */
  buildingCount: z.number().nullable(),
  /**
   * Totals printed for several buildings together that are not one building.
   * Never copied onto a building. Empty on batches stored before the field.
   */
  areas: ffuAreasSchema.default(() => emptyFfuAreas()),
  buildings: z.array(ffuBuildingSchema),
  /** One page per group of buildings, in pick order. Absent when none fits. */
  sitePlanUnderlays: z.array(ffuSitePlanUnderlaySchema).optional(),
  /**
   * True when the site model or a plan showing several buildings placed them.
   * Absent on batches stored before the field.
   */
  buildingsPlacedFromSite: z.boolean().optional(),
  /** What the correction call fixed. Absent when it did not run. */
  corrections: z.array(ffuCorrectionSchema).optional(),
});

export type FfuOutputFacts = z.infer<typeof ffuOutputFactsSchema>;

export const emptyFfuOutputFacts = (): FfuOutputFacts => ({
  name: null,
  description: null,
  location: null,
  storeyCount: null,
  buildingCount: null,
  areas: emptyFfuAreas(),
  buildings: [],
});
